import { useState, useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import Caret from './Caret';

interface MultiSelectDropdownProps {
  options: string[];
  selected: Set<string>;
  onToggle: (option: string) => void;
  /** When provided, renders a "Select all"/"Deselect all" action row in the open list */
  onSetAll?: (selectAll: boolean) => void;
  disabled?: boolean;
  placeholder?: string;
}

export default function MultiSelectDropdown({
  options,
  selected,
  onToggle,
  onSetAll,
  disabled = false,
  placeholder = 'Select...',
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const count = selected.size;

  // Read-only: just show chips
  if (disabled) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {count === 0 && <span className="text-muted-foreground">{placeholder}</span>}
        {[...selected].map((item) => (
          <span
            key={item}
            className="px-2.5 py-1 rounded-full bg-primary-muted text-foreground text-[13px] whitespace-nowrap"
          >
            {item}
          </span>
        ))}
      </div>
    );
  }

  const triggerLabel =
    count === 0
      ? placeholder
      : count <= 2
        ? [...selected].join(', ')
        : `${count} formats selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`w-full flex items-center justify-between gap-2 border border-border/60 rounded-md px-3 py-2 bg-card text-foreground text-sm cursor-pointer text-left outline-none transition-colors focus:ring-2 focus:ring-ring/30 ${
          open ? 'border-primary rounded-b-none' : ''
        }`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={count === 0 ? 'text-muted-foreground' : ''}>{triggerLabel}</span>
        <span className="shrink-0 grid place-items-center">
          <Caret up={open} />
        </span>
      </button>

      {open && (
        <ul className="absolute z-50 top-full left-0 right-0 max-h-60 overflow-y-auto list-none m-0 py-1 border border-primary border-t-0 rounded-b-md bg-card shadow-xl" role="listbox">
          {onSetAll && options.length > 0 && (
            <li className="border-b border-border/60">
              <button
                type="button"
                className="w-full cursor-pointer bg-transparent border-0 px-3 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-secondary"
                onClick={() => onSetAll(selected.size < options.length)}
              >
                {selected.size < options.length ? 'Select all' : 'Deselect all'}
              </button>
            </li>
          )}
          {options.map((opt) => {
            const checked = selected.has(opt);
            return (
              <li key={opt} role="option" aria-selected={checked}>
                <label className="flex items-center gap-2.5 px-3 py-2 cursor-pointer select-none transition-colors duration-100 hover:bg-secondary">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(opt)}
                    className="hidden"
                  />
                  <span
                    className={`shrink-0 w-[22px] h-[22px] rounded border-2 grid place-items-center transition-colors duration-100 ${
                      checked
                        ? 'border-primary bg-primary'
                        : 'border-border bg-card'
                    }`}
                  >
                    {checked && <Check size={13} className="text-primary-foreground" />}
                  </span>
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">{opt}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
